// Client-side router + URL parsing.
//
// `navigate()` mutates `state.route`/`state.tab`, then triggers a re-render.
// The render and toast functions are injected by the legacy module (so the
// router doesn't have to import the entire view layer during the refactor).
//
// Special-route parsing peels invite/login/guest/roster_join paths off the
// URL before the SPA picks a regular tab; auth bootstrap consumes the result.

import { state, TAB_ROUTES } from "../state";
import { isAdmin } from "../auth";

export interface SpecialRoute {
  kind: "invite" | "login" | "guest" | "roster_join" | null;
  memberId?: string;
  token?: string;
  eventId?: string;
}

// pathname을 옵션 인자로 받아 단위 테스트가 window.location 없이도 검증 가능.
// 런타임에서는 인자를 비워두면 window.location.pathname을 기본값으로.
export function resolveClubSlugFromUrl(
  pathname: string = window.location.pathname,
): string | null {
  const match = pathname.match(/^\/c\/([a-z0-9][a-z0-9-]*)\/?/i);
  return match ? match[1].toLowerCase() : null;
}

// Returns { kind: 'invite'|'login'|'guest'|'roster_join'|null, ... } parsed from the URL.
export function parseSpecialRoute(
  pathname: string = window.location.pathname,
  search: string = window.location.search,
): SpecialRoute {
  const invite = pathname.match(
    /^\/c\/[a-z0-9][a-z0-9-]*\/invite\/([a-zA-Z0-9_-]+)\/([A-Za-z0-9_-]+)\/?$/,
  );
  if (invite) return { kind: "invite", memberId: invite[1], token: invite[2] };
  const login = pathname.match(/^\/c\/[a-z0-9][a-z0-9-]*\/login\/?$/);
  if (login) return { kind: "login" };
  const guest = pathname.match(
    /^\/c\/[a-z0-9][a-z0-9-]*\/guest\/events\/([a-zA-Z0-9_-]+)\/?$/,
  );
  if (guest) {
    const token = new URLSearchParams(search).get("token") || "";
    return { kind: "guest", eventId: guest[1], token };
  }
  const roster = pathname.match(
    /^\/c\/[a-z0-9][a-z0-9-]*\/roster-join\/([A-Za-z0-9_-]+)\/?$/,
  );
  if (roster) return { kind: "roster_join", token: roster[1] };
  return { kind: null };
}

export function goToClubHome(): void {
  if (!state.config.clubSlug) return;
  const home = `/c/${state.config.clubSlug}/`;
  if (window.location.pathname !== home) {
    window.history.replaceState({}, "", home);
  }
}

// Render + toast injected by legacy module (avoids importing the view layer here).
type RenderFn = () => Promise<void>;
type ToastFn = (msg: string) => void;

let _render: RenderFn | null = null;
let _toast: ToastFn | null = null;

export function setRouterDeps(deps: { render: RenderFn; toast: ToastFn }): void {
  _render = deps.render;
  _toast = deps.toast;
}

export function navigate(route: any): void {
  state.route = route;
  if (TAB_ROUTES.has(route.name)) state.tab = route.name;
  if (!_render) {
    throw new Error("navigate() called before setRouterDeps() registered render()");
  }
  _render().catch((err: any) => {
    console.error(err);
    _toast?.(err.message || "알 수 없는 오류");
  });
}

export function goBack(): void {
  navigate({ name: state.tab || "home" });
}

export function refreshTopbar(): void {
  const back = document.querySelector(".topbar__btn--back") as HTMLElement | null;
  const title = document.getElementById("topbar-title");
  const iconGuest = document.getElementById("topbar-icon-guest");
  const iconAdmin = document.getElementById("topbar-icon-admin");
  const dot = document.getElementById("topbar-admin-dot");
  if (!back || !title || !iconGuest || !iconAdmin || !dot) return;

  if (state.route.name === "home") {
    back.hidden = true;
    title.textContent = state.club?.name || "페어매치";
  } else if (state.route.name === "events") {
    back.hidden = true;
    title.textContent = "일정";
  } else if (state.route.name === "stats") {
    back.hidden = true;
    title.textContent = "통계";
  } else if (state.route.name === "event") {
    back.hidden = false;
    title.textContent = "일정 정보";
  } else if (state.route.name === "members") {
    back.hidden = false;
    title.textContent = "회원 관리";
  } else if (state.route.name === "club_settings") {
    back.hidden = false;
    title.textContent = "클럽 설정";
  } else if (state.route.name === "member_login") {
    back.hidden = false;
    title.textContent = "회원 로그인";
  } else if (state.route.name === "invite_activate") {
    back.hidden = true;
    title.textContent = "회원 가입";
  } else if (state.route.name === "roster_join") {
    back.hidden = true;
    title.textContent = "회원 가입";
  } else if (state.route.name === "my_record") {
    back.hidden = false;
    title.textContent = "내 프로필";
  } else if (state.route.name === "guest_event") {
    back.hidden = true;
    title.textContent = "게스트 보기";
  }
  const menuBtn = document.querySelector(".topbar__btn--menu") as HTMLElement | null;
  if (state.route.name === "guest_event") {
    iconGuest.hidden = true;
    iconAdmin.hidden = true;
    dot.hidden = true;
    if (menuBtn) menuBtn.hidden = true;
    return;
  }
  if (menuBtn) menuBtn.hidden = false;
  if (isAdmin()) {
    iconGuest.hidden = true;
    iconAdmin.hidden = false;
    dot.hidden = false;
  } else {
    iconGuest.hidden = false;
    iconAdmin.hidden = true;
    dot.hidden = true;
  }
}
